import { useEffect, useState } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useMFAStatus } from "../hooks/useMFAStatus";
import { supabase } from "../lib/supabase";

const ProtectedRoute = ({ children }) => {
  const {
    user,
    loading,
    allowedRoutes,
    routesLoading,
    selectedOrg,
    needsOrgSelection,
  } = useAuth();

  const { isMFAVerified, aalLevel, loading: mfaLoading } = useMFAStatus();
  const location = useLocation();

  const [isFirstTimeUser, setIsFirstTimeUser] = useState(null);
  const [checking, setChecking] = useState(true);

  const mfaEnabled = import.meta.env.VITE_ENABLE_MFA === "true";
  const bypassRoutes = [
    "/verify-otp",
    "/security/mfa",
    "/security/mfa-reset",
    "/change-password",
  ];
  const shouldBypass = bypassRoutes.includes(location.pathname);

  // Check first_login flag
  useEffect(() => {
    const checkFirstLogin = async () => {
      if (!user?.id) {
        setChecking(false);
        setIsFirstTimeUser(false);
        return;
      }

      try {
        const { data: profile } = await supabase
          .from("user_profiles")
          .select("first_login")
          .eq("id", user.id)
          .single();

        const isFirstTime = profile?.first_login === 0;

        setIsFirstTimeUser(isFirstTime);
      } catch (err) {
        console.error("Error checking first_login:", err);
        // Fallback: check metadata
        const metadata = user.user_metadata || {};
        setIsFirstTimeUser(metadata.temp_password === true);
      } finally {
        setChecking(false);
      }
    };

    checkFirstLogin();
  }, [user?.id]);

  // Loading
  if (loading || mfaLoading || checking || isFirstTimeUser === null) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-background">
        <div className="w-8 h-8 rounded-full border-4 border-primary/20 border-t-primary animate-spin" />
      </div>
    );
  }

  // Bypass
  if (shouldBypass) return children;

  // Not logged in
  if (!user) {
    return <Navigate to="/user-login" replace />;
  }

  // First Time User → Go to Change Password
  if (isFirstTimeUser) {
    return <Navigate to="/change-password" replace />;
  }

  // MFA
  if (mfaEnabled) {
    if (aalLevel === null) return <Navigate to="/security/mfa" replace />;
    if (!isMFAVerified) return <Navigate to="/verify-otp" replace />;
  }

  // Org Selection
  if (needsOrgSelection && !selectedOrg) {
    return <Navigate to="/org-selection" replace />;
  }

  // Permission loading
  if (routesLoading && allowedRoutes.length === 0) {
    return <div className="text-center mt-10">Loading permissions...</div>;
  }

  // Route permission check
  const currentPath = location.pathname.replace(/\/$/, "");
  const publicRoutes = ["/dashboard"];

  const isAllowed =
    publicRoutes.includes(currentPath) ||
    allowedRoutes?.some((route) => {
      const regex = new RegExp(
        "^" + route.replace(/\/$/, "").replace(/:\w+/g, "[^/]+") + "$",
      );
      return regex.test(currentPath);
    });

  if (!isAllowed) {
    return <Navigate to="/unauthorized" replace />;
  }

  return children;
};

export default ProtectedRoute;
